<template>
	<el-dialog 
		destroy-on-close
		v-model="isVisible" 		
		@closed="onDialogClosed"
		:style="{'min-height': `${dialogWindowMinHeight}px`}"
		style="
			min-width: 800px;
			width: calc(40% - 2rem);
			height: calc(60% - 2rem)">
			<div ref="d">				
				<el-row>
					<el-col :span="14" style="text-align: right;">
						<div class="base-form filter" style="margin-left: 0">
							<el-input v-model="tableSearchQuery" style="max-width: 600px" placeholder="Поиск по имени, фамилии, отчеству, телефону или e-mail"
								class="input-with-select">
								<template #append>
									<el-button :icon="searchIcon" />
								</template>
							</el-input>
						</div>
					</el-col>
				</el-row>

				<el-row>
					<el-col :span="8">
						<div class="base-form filter" style="margin-left: 0">
							<el-select
								v-model="tablePositionsFilter"
								multiple
								clearable
								placeholder="Фильтр по должностям"
								@change="fetchEmployeesAsync">
								<el-option v-for="p in employeePositions"
									:key="p.id"
									:value="p.id"
									:label="p.name">
								</el-option>
							</el-select>
						</div>
					</el-col>
					<el-col :span="2">
						&nbsp;
					</el-col>
					<el-col :span="8">
						<div class="base-form filter" style="margin-left: 0">
							<el-select
								v-model="tableBranchesFilter"
								disabled
								clearable
								placeholder="Фильтр по подразделению"
								@change="fetchEmployeesAsync">
								<el-option v-for="p in employerBranches"
									:key="p.id"
									:value="p.id"
									:label="p.name">
								</el-option>
							</el-select>
						</div>
					</el-col>
				</el-row>

				<el-row>
					<el-col>
						<div class="base-form filter" style="margin-left: 0">
							<el-button 
								@click="onAssignEmployeesClick" 
								:disabled="!selectedAssignees.length" 
								type="primary">
									Назначить {{ (selectedAssignees.length ? `(${selectedAssignees.length})` : '') }}
							</el-button>
						</div>
					</el-col>
				</el-row>

				<el-row>
					<el-scrollbar :style="{width: '100%', 'height': `${scrollableContainerHeight}px` }">
						<el-table 			
							ref="employeesTableRef"		
							row-key="id"
							v-loading="tableLoading" 
							sortable="custom" 
							:data="tableData"
							:show-header="false"
							:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
							@sort-change="onTableSortingChange"
							@cell-click="onTableCellClick"
							:style="{height: `${scrollableContainerHeight * .8}px`}">
							<template #empty>
								Список сотрудников пуст ☹️
							</template>
							<el-table-column width="40">			
								<template #default="scope">
									<el-checkbox @change="(_, e) => onTableRowSelectionChange(e)" v-model="scope.row.isAssigneeSelected" />
								</template>
							</el-table-column>
							<el-table-column label="Сотрудник" prop="employee">
								<template #default="scope">
									{{ scope.row.employee.lastname }} {{ scope.row.employee.firstname }}							
									<div>
										<el-text type="info" size="small">{{ scope.row.employee.position.name }}{{ !!scope.row.employee.positionGrade?.name ? `, ${scope.row.employee.positionGrade.name}` : '' }}</el-text>
									</div>
									<div>
										<el-tag v-if="!!scope.row.employee.fireDate" size="small" type="warning" effect="plain">Уволен</el-tag>
									</div>
								</template>
							</el-table-column>
						</el-table>						
					</el-scrollbar>					
				</el-row>
			</div>
	</el-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag } from 'element-plus';
import OrderJobAssigneeModel from '@/models/OrderJobAssigneeModel';
import { WatchStopHandle, watch } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import OrderJobAssigneeViewModel from '@/ViewModels/OrderJobAssigneeViewModel';
import EmployeesApiHelper from '@/helpers/api/EmployeesApiHelper';
import LegalEntityBranchModel from '@/models/LegalEntityBranchModel';
import LegalEntityApiHelper from '@/helpers/api/LegalEntityApiHelper';

@Options({	
	components: {
		ElButton,
		ElTable,
		ElTag
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		defaultBranchId: {
			type: Number,
			default: null
		}
	},
	emits: ['assigned', 'closed'],
})
export default class OrderJobAssigneesPickerModalComponent extends Vue {
	/** props */
	visible!: boolean;
	defaultBranchId!: number | null;

	/** constants */
	dialogWindowMinHeight = 640;
	circleIcon = CirclePlus;
	searchIcon = Search;
	employeePositions = new Array<any>();
	employerBranches = new Array<LegalEntityBranchModel>();

	/** models */	
	tableLoading = false;
	tableData: Array<OrderJobAssigneeViewModel> = [];	
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tablePositionsFilter: Array<number> = [];	
	tableBranchesFilter: Array<number> = [];	
	tableSearchQuery = '';
	tableSortColumn = 'lastname';
	tableSortOrder = 'ascending';

	isVisible = false;
	orderJobVmId = 0;
	selectedAssignees = new Array<OrderJobAssigneeModel>();
	scrollableContainerHeight = 0;

	watchTableSearchQuery: WatchStopHandle | null = null;

	onDialogClosed() {		
		this.hide();
		this.$emit('closed');
	}

	onAssignEmployeesClick() {
		this.$emit('assigned',
			this.orderJobVmId,
			this.selectedAssignees
		);

		this.hide();
	}

	async fetchEmployeesAsync() {
		try {
			this.tableLoading = true;
			const employees = await new EmployeesApiHelper().getEmployeesAsync(
				store.getters.jwt,
				this.tableSortColumn, 
				this.tableSortOrder == 'ascending',
				this.tableSearchQuery,
				this.tablePositionsFilter,
				this.tableBranchesFilter
			);

			if (!employees) {
				this.tableData = new Array<OrderJobAssigneeViewModel>();
				return;
			}
			
			this.tableData = employees.map(employee => new OrderJobAssigneeViewModel({employee}));
			
			if (this.selectedAssignees?.length) {

				this.tableData.forEach(r => {
					const selectedAssignee = this.selectedAssignees.find(_ => _.employee!.id == r.employee!.id);
					if (selectedAssignee) {						
						r.isAssigneeSelected = true;
					}
				});
			}
		} catch (e: any) {
			console.error('Error loading employees list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async fetchEmployeePositionsAsync() {
		try {
			const jwt = store.getters.jwt
			const employeePositionsHttpClient = axiosHelper.createInstance(jwt);
			const response = await employeePositionsHttpClient.get('/api/employees/positions');
			this.employeePositions = response.data;			
		} catch (e: any) {
			console.error('Error loading employee positions list', e);
		}
	}

	async fetchEmployerBranchesAsync() {
		const branches = await new LegalEntityApiHelper(store.getters.jwt).getExecutiveBranchesAsync();
		this.employerBranches = branches ?? [];
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder ==  a.order;
		this.fetchEmployeesAsync();
	}

	onTableRowSelectionChange(e: Event) {
		e.stopPropagation();
		this.selectedAssignees = [];
		this.tableData.forEach(i => {
			if (i.isAssigneeSelected) {
				this.selectedAssignees.push(i);
			}
		});
	}

	onTableCellClick(row: OrderJobAssigneeViewModel, column: any, _: any, e: Event) {
		e.stopPropagation();
		if (column.property == 'employee') {
			const assignee = this.tableData.find(_ => _.employee!.id == row.employee!.id)!;
			assignee.isAssigneeSelected = !assignee.isAssigneeSelected;
			this.onTableRowSelectionChange(e);
		}
	}

	setScollableWindowHeight() {		
		this.$nextTick().then(() => {
			const w = this.$refs.d as HTMLElement;

			if (!w) {
				this.scrollableContainerHeight = this.dialogWindowMinHeight;
				return;
			}

			const dialogWindow = w.closest('.el-dialog');

			if (!dialogWindow) {
				this.scrollableContainerHeight = this.dialogWindowMinHeight;
				return;
			}
			
			const dialogWindowHeight = dialogWindow.getBoundingClientRect().height;

			if (dialogWindowHeight < this.dialogWindowMinHeight) {
				this.scrollableContainerHeight = this.dialogWindowMinHeight;
				return;
			}

			this.scrollableContainerHeight = Math.round(dialogWindowHeight! * .9);			
		});
	}

	/** exposed functions */
	setAssignees(assignees?: Array<OrderJobAssigneeModel>) {
		if (!assignees?.length) {
			this.selectedAssignees = [];
			return this;
		}
		
		this.selectedAssignees = assignees;
		return this;
	}

	/** exposed functions */
	setOrderJobId(orderJobVmId: number) {
		this.orderJobVmId = orderJobVmId;

		return this;
	}

	/** exposed functions */
	show() {
		this.fetchEmployeesAsync();
		this.fetchEmployeePositionsAsync();
		this.fetchEmployerBranchesAsync();

		if (this.defaultBranchId) {
			this.tableBranchesFilter = [this.defaultBranchId];
		}

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchEmployeesAsync()
		);

		this.isVisible = true;
		this.setScollableWindowHeight();

		window.addEventListener('resize', this.setScollableWindowHeight);

		return this;
	}

	/** exposed functions */
	hide() {
		window.removeEventListener('resize', this.setScollableWindowHeight);
		this.employeePositions = [];
		this.tableSearchQuery = '';		
		this.selectedAssignees = [];
		this.tableData = [];
		this.isVisible = false;
		return this;
	}
}
</script>